<template>
  <div class="author-badge">
    <div class="author-badge__avatar">
      <img
        v-if="shouldShowAvatar"
        ref="avatarImageEl"
        :src="avatarUrl"
        :alt="`Photo de ${authorName}`"
        class="author-badge__image"
        @error="onAvatarLoadError" />
      <span
        v-else
        class="author-badge__initials">
        {{ initials }}
      </span>
    </div>

    <div class="author-badge__content">
      <p class="author-badge__title">Article écrit par {{ authorName }}</p>
      <p class="author-badge__subtitle">Lire d'autres articles écrits par {{ authorName }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps({
  authorName: {
    type: String,
    required: true,
  },
  avatarUrl: {
    type: String,
    default: '',
  },
})

const avatarImageEl: Ref<HTMLImageElement | null> = ref(null)
const hasAvatarLoadError: Ref<boolean> = ref(false)

const shouldShowAvatar: ComputedRef<boolean> = computed(
  (): boolean => Boolean(props.avatarUrl) && !hasAvatarLoadError.value,
)

const initials = computed(() => {
  return props.authorName
    .split(' ')
    .map((n) => n.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
})

/**
 * Bascule sur les initiales quand la photo ne se charge pas (URL cassée, 404).
 * @returns {void}
 */
function onAvatarLoadError(): void {
  hasAvatarLoadError.value = true
}

watch(
  (): string => props.avatarUrl,
  (): void => {
    hasAvatarLoadError.value = false
  },
)

onMounted((): void => {
  // Échec survenu avant l'hydratation : l'événement `error` est déjà passé.
  if (avatarImageEl.value?.complete && avatarImageEl.value.naturalWidth === 0) {
    hasAvatarLoadError.value = true
  }
})
</script>

<style scoped>
.author-badge {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  padding: 14px 22px 14px 14px;
  background-color: #1a1d24;
  border-radius: 12px;
  max-width: 100%;
}

.author-badge__avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4aa3ff 0%, #2b7fd9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.author-badge__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.author-badge__initials {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.author-badge__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.author-badge__title {
  margin: 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.3;
}

.author-badge__subtitle {
  margin: 0;
  color: #a8adb5;
  font-size: 13px;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.3;
}
</style>
